import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, X, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../utils/api';

const CATS = ['All','fleet','machinery','operations','team','before-after'];

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('All');
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get('/api/gallery?published_only=true').then(r => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const filtered = cat === 'All' ? items : items.filter(i => i.category === cat);
  const lbIdx = lightbox !== null ? filtered.findIndex(i => i.id === lightbox) : -1;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-apex-blue-700 text-white px-4 py-5">
        <div className="max-w-5xl mx-auto">
          <Link to="/" className="flex items-center gap-2 text-white/70 text-sm mb-4 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back to Home</Link>
          <h1 className="text-3xl font-black">Our Gallery</h1>
          <p className="text-white/60 mt-1">Real photos from real jobs</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Category filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {CATS.map(c => (
            <button key={c} onClick={() => setCat(c)} className={`px-4 py-2 rounded-xl text-sm font-bold border-2 whitespace-nowrap shrink-0 ${cat===c?'bg-apex-blue-700 border-apex-blue-700 text-white':'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
              {c === 'All' ? 'All Photos' : c.charAt(0).toUpperCase() + c.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading gallery...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-200">
            <p className="text-4xl mb-3">📷</p>
            <p className="font-bold text-gray-600">Gallery coming soon</p>
            <p className="text-sm mt-1">Check back shortly — our team is uploading photos</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map(item => (
              <button key={item.id} onClick={() => setLightbox(item.id)} className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gray-200 group text-left focus:outline-none">
                <img src={item.url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={e => { e.target.src=''; e.target.style.display='none'; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white text-xs font-bold truncate">{item.title}</p>
                    <p className="text-white/60 text-[10px] capitalize">{item.category}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && lbIdx >= 0 && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 bg-white/20 text-white p-2 rounded-full hover:bg-white/30" onClick={() => setLightbox(null)}><X className="w-5 h-5" /></button>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 text-white p-3 rounded-full hover:bg-white/30" onClick={e => { e.stopPropagation(); setLightbox(filtered[Math.max(0, lbIdx-1)].id); }}>
            <ChevronLeft className="w-5 h-5" />
          </button>
          <img src={filtered[lbIdx].url} alt={filtered[lbIdx].title} className="max-w-full max-h-[85vh] rounded-xl object-contain" onClick={e => e.stopPropagation()} />
          <button className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 text-white p-3 rounded-full hover:bg-white/30" onClick={e => { e.stopPropagation(); setLightbox(filtered[Math.min(filtered.length-1, lbIdx+1)].id); }}>
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-center">
            <p className="font-bold text-sm">{filtered[lbIdx].title}</p>
            <p className="text-white/50 text-xs">{lbIdx+1} / {filtered.length}</p>
          </div>
        </div>
      )}
    </div>
  );
}